import React, { createContext, useContext, useEffect, useState } from 'react';
import authService from '../services/authService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        const result = await authService?.getUserProfile(userId);
        if (result?.success && result?.profile) {
          setUserProfile(result?.profile);
        }
      } catch (error) {
        console.error('Profile load error:', error);
      } finally {
        setProfileLoading(false);
      }
    },

    clear() {
      setUserProfile(null);
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initializeSession = async () => {
      try {
        const result = await authService.getCurrentSession();
        if (!mounted) {
          return;
        }

        const sessionUser = result?.session?.user ?? null;
        setUser(sessionUser);
        setLoading(false);

        if (sessionUser?.id) {
          profileOperations.load(sessionUser.id);
        } else {
          profileOperations.clear();
        }
      } catch (error) {
        if (!mounted) {
          return;
        }

        setUser(null);
        setLoading(false);
        profileOperations.clear();
      }
    };

    initializeSession();

    return () => {
      mounted = false;
    };
  }, []);

  // Enhanced auth methods
  const signIn = async (username, password) => {
    try {
      const result = await authService?.signIn(username, password);
      if (result?.success) {
        const sessionUser = result?.data?.session?.user ?? null;
        setUser(sessionUser);
        if (sessionUser?.id) {
          await profileOperations.load(sessionUser.id);
        }
      }
      return result;
    } catch (error) {
      return { success: false, error: 'Errore di rete. Riprova.' };
    }
  };

  const signOut = async () => {
    try {
      const result = await authService?.signOut();
      if (result?.success) {
        setUser(null);
        profileOperations?.clear();
      }
      return result;
    } catch (error) {
      return { success: false, error: 'Errore di rete. Riprova.' };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { success: false, error: 'Nessun utente autenticato' };
    
    try {
      const result = await authService.updateUserProfile(user.id, updates);
      if (!result?.success) {
        return result;
      }

      if (result?.data) {
        setUserProfile(result.data);
      }

      return result;
    } catch (error) {
      return { success: false, error: 'Errore di rete. Riprova.' };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
